import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface SkinScienceProps {
  onNext: () => void;
  productsTried?: string[];
}

const SkinScience = ({ onNext, productsTried = [] }: SkinScienceProps) => {
  const usedCreams = productsTried.includes("creams");
  const headline = usedCreams
    ? "Daarom werkten je crèmes niet"
    : "Waarom de meeste producten niet werken";
  const subtitle = usedCreams
    ? "Waarom crèmes niet werken"
    : "Dit is wat er onder het oppervlak gebeurt";
  const creamLabel = usedCreams ? "Jouw crème stopt hier" : "Crème stopt hier";
  const conclusionLine1 = usedCreams
    ? "Het probleem was nooit jouw doorzettingsvermogen."
    : "Het probleem is niet welk product je kiest.";
  const conclusionLine2 = usedCreams
    ? "Het was de methode die niet werkte."
    : "Het is dat de meeste producten niet diep genoeg komen.";
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 800),
      setTimeout(() => setPhase(2), 2200),
      setTimeout(() => setPhase(3), 3800),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="text-center">
      <h2 className="text-2xl md:text-4xl font-serif font-medium text-foreground mb-1 leading-tight">
        Dit is wat er écht gebeurt
      </h2>
      <p className="text-foreground/80 font-sans text-lg md:text-xl mb-4 md:mb-8">
        {subtitle}
      </p>

      {/* SVG Skin Cross-Section */}
      <div className="relative rounded-2xl overflow-hidden shadow-warm border border-border mb-4 md:mb-6 bg-white md:max-h-[420px] md:flex md:items-center md:justify-center">
        <svg
          viewBox="0 0 400 520"
          className="w-full"
          aria-label="Doorsnede van de huid"
        >
          {/* === EPIDERMIS — top layer, textured skin tone === */}
          <defs>
            {/* Skin texture pattern */}
            <pattern id="skinTexture" patternUnits="userSpaceOnUse" width="20" height="20">
              <rect width="20" height="20" fill="hsl(30 45% 82%)" />
              <circle cx="5" cy="8" r="1.5" fill="hsl(30 30% 78%)" opacity="0.5" />
              <circle cx="15" cy="4" r="1" fill="hsl(30 25% 76%)" opacity="0.4" />
              <circle cx="10" cy="16" r="1.2" fill="hsl(28 35% 79%)" opacity="0.3" />
            </pattern>
            {/* Collagen fiber pattern for dermis */}
            <pattern id="collagenFibers" patternUnits="userSpaceOnUse" width="40" height="30">
              <rect width="40" height="30" fill="hsl(12 40% 68%)" />
              <path d="M0 15 Q10 8 20 15 Q30 22 40 15" stroke="hsl(12 35% 62%)" strokeWidth="1.5" fill="none" opacity="0.5" />
              <path d="M0 8 Q10 2 20 8 Q30 14 40 8" stroke="hsl(15 30% 72%)" strokeWidth="1" fill="none" opacity="0.3" />
              <path d="M0 22 Q10 16 20 22 Q30 28 40 22" stroke="hsl(10 38% 60%)" strokeWidth="1" fill="none" opacity="0.4" />
            </pattern>
            {/* Fat cell pattern for subcutis */}
            <pattern id="fatCells" patternUnits="userSpaceOnUse" width="50" height="45">
              <rect width="50" height="45" fill="hsl(25 30% 52%)" />
              <circle cx="15" cy="15" r="10" fill="hsl(25 28% 56%)" stroke="hsl(25 25% 48%)" strokeWidth="0.8" opacity="0.6" />
              <circle cx="40" cy="12" r="8" fill="hsl(25 32% 58%)" stroke="hsl(25 25% 48%)" strokeWidth="0.8" opacity="0.5" />
              <circle cx="25" cy="35" r="11" fill="hsl(25 26% 54%)" stroke="hsl(25 25% 48%)" strokeWidth="0.8" opacity="0.55" />
              <circle cx="5" cy="38" r="7" fill="hsl(25 30% 57%)" stroke="hsl(25 25% 48%)" strokeWidth="0.8" opacity="0.45" />
            </pattern>
            {/* Glow filter for Adelira arrow */}
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Surface skin line — wavy top */}
          <path
            d="M0 30 Q50 20 100 28 Q150 36 200 26 Q250 18 300 30 Q350 38 400 25 L400 0 L0 0 Z"
            fill="hsl(32 50% 88%)"
          />

          {/* EPIDERMIS layer */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <path
              d="M0 30 Q50 20 100 28 Q150 36 200 26 Q250 18 300 30 Q350 38 400 25 L400 140 L0 140 Z"
              fill="url(#skinTexture)"
            />
            {/* Pores / hair follicles */}
            <line x1="80" y1="25" x2="82" y2="70" stroke="hsl(30 20% 65%)" strokeWidth="1.5" opacity="0.4" />
            <line x1="200" y1="24" x2="201" y2="65" stroke="hsl(30 20% 65%)" strokeWidth="1.5" opacity="0.35" />
            <line x1="320" y1="28" x2="319" y2="72" stroke="hsl(30 20% 65%)" strokeWidth="1.5" opacity="0.4" />

            {/* Label */}
            <text x="200" y="75" textAnchor="middle" style={{ fontSize: 13, fontFamily: "Inter, sans-serif", fill: "hsl(20 25% 40%)", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase" as const }}>
              Epidermis
            </text>
            <text x="200" y="97" textAnchor="middle" style={{ fontSize: 17, fontFamily: "Inter, sans-serif", fill: "hsl(20 20% 35%)", fontWeight: 500 }}>
              Beschermlaag
            </text>
          </motion.g>

          {/* Barrier line between epidermis & dermis */}
          <motion.line
            x1="0" y1="140" x2="400" y2="140"
            stroke="hsl(20 30% 55%)"
            strokeWidth="2"
            strokeDasharray="6,4"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 0.3, duration: 1 }}
          />

          {/* DERMIS layer */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <rect x="0" y="140" width="400" height="180" fill="url(#collagenFibers)" />

            {/* Wavy collagen bundles */}
            <path d="M20 200 Q70 185 120 205 Q170 220 220 200 Q270 180 320 210 Q370 230 400 195" stroke="hsl(15 40% 58%)" strokeWidth="2.5" fill="none" opacity="0.6" />
            <path d="M0 240 Q50 225 100 245 Q150 260 200 240 Q250 220 300 250 Q350 265 400 235" stroke="hsl(12 35% 55%)" strokeWidth="2" fill="none" opacity="0.5" />

            {/* Blood vessel hint */}
            <path d="M60 170 Q80 190 70 220 Q65 240 75 260" stroke="hsl(0 50% 55%)" strokeWidth="1.5" fill="none" opacity="0.3" />
            <path d="M300 175 Q310 200 295 230 Q290 255 305 280" stroke="hsl(0 50% 55%)" strokeWidth="1.5" fill="none" opacity="0.25" />

            <text x="200" y="185" textAnchor="middle" style={{ fontSize: 13, fontFamily: "Inter, sans-serif", fill: "hsl(0 0% 100%)", fontWeight: 700, letterSpacing: "0.15em" }}>
              DERMIS
            </text>
            <text x="200" y="210" textAnchor="middle" style={{ fontSize: 17, fontFamily: "Inter, sans-serif", fill: "hsl(0 0% 95%)", fontWeight: 500 }}>
              Hier wordt collageen aangemaakt
            </text>
          </motion.g>

          {/* SUBCUTIS layer */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <rect x="0" y="320" width="400" height="200" fill="url(#fatCells)" />

            <text x="200" y="370" textAnchor="middle" style={{ fontSize: 13, fontFamily: "Inter, sans-serif", fill: "hsl(0 0% 90%)", fontWeight: 700, letterSpacing: "0.15em" }}>
              SUBCUTIS
            </text>
            <text x="200" y="395" textAnchor="middle" style={{ fontSize: 17, fontFamily: "Inter, sans-serif", fill: "hsl(0 0% 85%)", fontWeight: 500 }}>
              Vetweefsel
            </text>
          </motion.g>

          {/* === CREAM DROP — falls and stops at barrier === */}
          <AnimatePresence>
            {phase >= 1 && (
              <motion.g>
                {/* Cream droplet */}
                <motion.ellipse
                  cx="130" cy="10"
                  rx="14" ry="10"
                  fill="hsl(45 60% 90%)"
                  stroke="hsl(40 40% 75%)"
                  strokeWidth="1.5"
                  initial={{ cy: 10, opacity: 1 }}
                  animate={{ cy: 120, opacity: 1 }}
                  transition={{ duration: 1.2, ease: "easeIn" }}
                />
                {/* X mark where it stops */}
                <motion.g
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.3, type: "spring", stiffness: 200 }}
                >
                  <circle cx="130" cy="130" r="14" fill="hsl(350 60% 45%)" />
                  <text x="130" y="135" textAnchor="middle" style={{ fontSize: 16, fill: "white", fontWeight: 800 }}>✗</text>
                </motion.g>

                {/* "Crème stopt hier" label */}
                <motion.g
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.5, duration: 0.5 }}
                >
                  <text x="155" y="136" textAnchor="start" style={{ fontSize: 17, fontFamily: "Inter, sans-serif", fill: "hsl(350 60% 45%)", fontWeight: 700 }}>
                    {creamLabel}
                  </text>
                  
                </motion.g>
              </motion.g>
            )}
          </AnimatePresence>

          {/* === ADELIRA ARROW — penetrates deep into dermis === */}
          <AnimatePresence>
            {phase >= 2 && (
              <motion.g>
                {/* Glowing arrow line */}
                <motion.line
                  x1="280" y1="10" x2="280" y2="260"
                  stroke="hsl(38 58% 42%)"
                  strokeWidth="4"
                  strokeLinecap="round"
                  filter="url(#glow)"
                  initial={{ y2: 10 }}
                  animate={{ y2: 260 }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                />
                {/* Arrow head */}
                <motion.polygon
                  points="270,255 280,275 290,255"
                  fill="hsl(38 58% 42%)"
                  filter="url(#glow)"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.5 }}
                />

                {/* Checkmark at dermis */}
                <motion.g
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.8, type: "spring", stiffness: 200 }}
                >
                  <circle cx="280" cy="270" r="14" fill="hsl(140 55% 35%)" />
                  <text x="280" y="276" textAnchor="middle" style={{ fontSize: 16, fill: "white", fontWeight: 800 }}>✓</text>
                </motion.g>

                {/* "De juiste behandeling bereikt hier" label */}
                <motion.g
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 2, duration: 0.5 }}
                >
                  <text x="200" y="300" textAnchor="middle" style={{ fontSize: 16, fontFamily: "Inter, sans-serif", fill: "hsl(140 55% 35%)", fontWeight: 700 }}>
                    De juiste behandeling bereikt wél hier
                  </text>
                  
                </motion.g>
              </motion.g>
            )}
          </AnimatePresence>
        </svg>
      </div>

      {/* Conclusion */}
      <AnimatePresence>
        {phase >= 3 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="insight-card mb-5 md:mb-8">
              <p className="font-serif text-xl md:text-3xl text-foreground mb-2 leading-snug">
                {conclusionLine1}
              </p>
              <p className="font-serif text-2xl md:text-3xl text-foreground leading-snug" style={{ color: "hsl(30 50% 30%)" }}>
                {conclusionLine2}
              </p>
            </div>

            <button onClick={onNext} className="btn-cta">
              Ga verder
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SkinScience;
